"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import {
  DataTable,
  type DataTableProps,
  type DataTableSortStatus,
} from "mantine-datatable";
import { ActionIcon, Alert, Box, Button, Group, Text as MantineText, Tooltip, TextInput, Select, Loader, LoadingOverlay, Popover, Stack, Badge } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconShip, IconPlane, IconEdit, IconCopy, IconEye, IconInfoCircle, IconUpload, IconCircleCheck, IconRefresh, IconTrash, IconRestore, IconFileTypePdf, IconAlertTriangle } from "@tabler/icons-react";
import { CompanyNameCell } from "./CompanyNameCell";
import { StatusHistoryIconButton } from "./StatusHistoryModal";
import UnggahBerkasModal from "./UnggahBerkasModal";
import DeclarationPreviewModal from "./DeclarationPreviewModal";
import type { DocPabean, DocPabeanStatus } from "../../app/(dashboard)/doc-pabean/apis/models/doc-pabean-model";
import {
  StatusChip,
  getStatusChipStyle,
  getShipmentTypeChipStyle,
  getMotChipStyle,
} from "@components/commons/status-chip";
import { flagAssetUrl, toFlagCountryCode } from "@utils/country-flag";
import { resolveMot, type MotType } from "@utils/mot";
import { getHoverActionIconStyles } from "@components/commons/hover-action-icon-styles";
import useCopyDocPabean from "../../app/(dashboard)/doc-pabean/apis/use-cases/use-copy-doc-pabean";
import useSyncRespon from "../../app/(dashboard)/doc-pabean/apis/use-cases/use-sync-respon";
import useDeleteDocPabean from "../../app/(dashboard)/doc-pabean/apis/use-cases/use-delete-doc-pabean";
import useRestoreDocPabean from "../../app/(dashboard)/doc-pabean/apis/use-cases/use-restore-doc-pabean";
import { useTheme } from "@contexts/ThemeContext";
import { useLanguage } from "@contexts/LanguageContext";
import {
  type DocPabeanTablePageKey,
  cleanText,
  isColumnHidden,
  vesselDateSummary,
} from "./list-prefs/doc-pabean-list-prefs";
import { useDocPabeanColumnToggle, useDocPabeanDisplayPrefs } from "./list-prefs/use-doc-pabean-list-prefs";

// Email-based access rules for BC 2.0 (doc-pabean / import)
// 'edit' = full edit access, 'view' = view-only (eye icon), undefined = no row access restriction (default edit)
const BC20_ACCESS_RULES: Record<string, 'edit' | 'view'> = {
  'anne.veronica@molgroup.com': 'view',
  'ikumi.shibata1@molgroup.com': 'view',
  'panji@gateway-id.com': 'view',
  'christina.vonny@molgroup.com': 'edit',
  'ari.wibowo@molgroup.com': 'edit',
  'femy.muthia@molgroup.com': 'edit',
  'lanny.apriliany@molgroup.com': 'edit',
  'mlg-id.seaimport01@molgroup.com': 'edit',
  'mlg-id.airport01@molgroup.com': 'edit',
};

// Email-based access rules for BC 3.0 (export)
const BC30_ACCESS_RULES: Record<string, 'edit' | 'view'> = {
  'panji@gateway-id.com': 'view',
  'christina.vonny@molgroup.com': 'edit',
  'ari.wibowo@molgroup.com': 'edit',
  'femy.muthia@molgroup.com': 'edit',
  'reza.haqqa@molgroup.com': 'edit',
  'ratri.puji@molgroup.com': 'edit',
  'prama.adi@molgroup.com': 'edit',
  'mlg-id.airexport01@molgroup.com': 'edit',
};

/**
 * Copy POSTs can take several seconds. Calling window.open() after await loses
 * the user-gesture token and browsers silently block the tab. Open a blank tab
 * synchronously on click, then navigate it once the UUID is known. If the
 * popup is blocked, fall back to same-tab navigation.
 */
function navigateCopiedDocument(targetUrl: string, newTab: Window | null) {
  const absoluteUrl = new URL(targetUrl, window.location.origin).href;
  if (newTab && !newTab.closed) {
    newTab.location.href = absoluteUrl;
    return;
  }
  window.location.assign(absoluteUrl);
}

/** Build draft-edit query string: optional isViewOnly + respon (namaProses). */
function buildDraftEditQuery(options: { isViewOnly?: boolean; respon?: string | null }) {
  const params = new URLSearchParams();
  if (options.isViewOnly) {
    params.set('isViewOnly', 'true');
  }
  const respon = options.respon?.trim();
  if (respon) {
    params.set('respon', respon);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const getUserEmail = (): string => {
  try {
    const raw = localStorage.getItem('authData');
    if (!raw) return '';
    return (JSON.parse(raw).email || '').toLowerCase();
  } catch {
    return '';
  }
};

const formatStatusDateTime = (dateStr: string): string => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

/**
 * MOT chip — the mode of transport as a word, not a bare icon.
 *
 * Replaces the old Mot column, which drew a ship or a plane and nothing else:
 * at 16px those two silhouettes are near-identical in a dense row, and the
 * fallback was wrong besides. Icon plus label, tinted from the same palette as
 * the shipment Type chip it now sits beside.
 */
function MotChip({ mot, themeColors }: { mot: MotType; themeColors: Record<string, string> }) {
  return (
    <span
      style={{
        ...getMotChipStyle(mot, themeColors),
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
        padding: "1px 5px",
        letterSpacing: 0.3,
      }}
    >
      {mot === "SEA" ? <IconShip size={12} /> : <IconPlane size={12} />}
      {mot}
    </span>
  );
}

function StatusCellWithInfo({
  statuses,
  themeColors,
}: {
  statuses: DocPabeanStatus[];
  themeColors: Record<string, any>;
}) {
  const [opened, setOpened] = useState(false);
  const { t } = useLanguage();
  const firstStatus = statuses[0];

  return (
    <Group gap={4} justify="center" wrap="nowrap">
      <span
        className="block px-1.5 py-0.5 text-[11px] font-semibold tracking-[0.3px] text-center"
        style={getStatusChipStyle(firstStatus.keterangan || '-', themeColors)}
      >
        {(firstStatus.keterangan || "-").toUpperCase()}
      </span>
      {statuses.length > 0 && (
        <Popover
          opened={opened}
          onChange={setOpened}
          position="bottom"
          withArrow
          shadow="md"
          width={320}
        >
          <Popover.Target>
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                setOpened((o) => !o);
              }}
              styles={getHoverActionIconStyles(themeColors, themeColors.info)}
              style={{ flexShrink: 0 }}
            >
              <IconInfoCircle size={14} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown
            style={{
              backgroundColor: themeColors.card,
              border: `1px solid ${themeColors.border}`,
              maxHeight: '50vh',
              overflowY: 'auto',
            }}
          >
            <Stack gap="xs">
              <MantineText size="sm" fw={600} style={{ color: themeColors.text, position: 'sticky', top: 0, backgroundColor: themeColors.card, zIndex: 1, paddingBottom: 4 }}>
                {t("docpabean.statusHistory")}
              </MantineText>
              {statuses.map((s, idx) => (
                <div
                  key={s.id}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 6,
                    backgroundColor: idx === 0 ? themeColors.tableRowHover : themeColors.formFields,
                    border: `1px solid ${themeColors.border}`,
                  }}
                >
                  <Group justify="space-between" align="center">
                    <MantineText size="xs" fw={500} style={{ color: themeColors.text }}>
                      {s.keterangan}
                    </MantineText>
                    {idx === 0 && (
                      <Badge
                        size="xs"
                        variant="outline"
                        radius="xs"
                        styles={{
                          root: {
                            backgroundColor: themeColors.formFields,
                            color: themeColors.textSecondary,
                            borderColor: themeColors.border,
                          },
                        }}
                      >
                        {t("docpabean.latest")}
                      </Badge>
                    )}
                  </Group>
                  {s.kodeRespon && (
                    <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                      {t("docpabean.respon")}: {s.kodeRespon} {s.nomorRespon ? `- ${s.nomorRespon}` : ""}
                    </MantineText>
                  )}
                  <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                    {formatStatusDateTime(s.createdAt)}
                  </MantineText>
                </div>
              ))}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  );
}

const PAGE_SIZES = [10, 20, 30, 40, 50];

const formatRelativeTime = (
  dateStr: string | null | undefined,
  tr?: (key: string) => string
): string | null => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return null;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const ago = tr ? tr("docpabean.ago") : "ago";
  if (mins < 1) return tr ? tr("docpabean.justNow") : "just now";
  if (mins < 60) return `${mins}m ${ago}`;
  if (hours < 24) {
    const remMins = mins % 60;
    return remMins > 0 ? `${hours}h ${remMins}m ${ago}` : `${hours}h ${ago}`;
  }
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h ${ago}` : `${days}d ${ago}`;
};

// Helper function to extract time from createdAt (e.g., "08:00", "12:30")
const getTimeFromCreatedAt = (createdAt: string | null | undefined): string | null => {
  if (!createdAt) return null;

  try {
    const createdDate = new Date(createdAt);
    const hours = createdDate.getHours().toString().padStart(2, '0');
    const minutes = createdDate.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return null;
  }
};

interface DocPabeanTableProps {
  data: DocPabean[];
  numberPageSize: number;
  numberPage: number;
  loading?: boolean;
  setNumberPageSize: (value: number) => void;
  onSearch?: (query: string) => void;
  totalPage?: number;
  onPageChange?: (page: number) => void;
  /**
   * Which page renders the table. Column and display choices from the Kolom
   * picker are stored per page key, so /doc-pabean and /monitoring-blokir keep
   * their own.
   */
  pageKey?: DocPabeanTablePageKey;
  /**
   * Parts of this page the API could not load (`incomplete` on the list
   * response). Their cells show "Gagal dimuat" and a notice offers a reload.
   */
  incomplete?: string[];
  onReload?: () => void;
}

/**
 * Trash icon with an inline confirm popover. Soft-deletes the doc pabean
 * entry (hidden from the list; the CEISA filing is untouched) and lets the
 * doc-pabeans query refetch via the delete hook's invalidation.
 */
function DeleteIconButton({ item, themeColors }: { item: DocPabean; themeColors: any }) {
  const [opened, setOpened] = useState(false);
  const { t } = useLanguage();
  const { deleteDocPabean, loading } = useDeleteDocPabean();

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md" width={240}>
      <Popover.Target>
        <Tooltip label={t("docpabean.tooltipDelete")}>
          <ActionIcon
            variant="subtle"
            onClick={() => setOpened((o) => !o)}
            style={{ color: "#e03131" }}
          >
            {loading ? <Loader size="xs" /> : <IconTrash size={18} />}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <MantineText size="sm">
            {t("docpabean.deleteConfirm").replace("{nomor}", item.nomorAju)}
          </MantineText>
          {/* Already filed at CEISA: deleting here only hides the row, so say
              so plainly rather than letting the two views diverge silently. */}
          {item.idHeader ? (
            <MantineText size="xs" c="orange" fw={500}>
              {t("docpabean.deleteFiledWarning")}
            </MantineText>
          ) : null}
          <Group justify="flex-end" gap="xs">
            <Button size="compact-xs" variant="default" onClick={() => setOpened(false)}>
              {t("docpabean.deleteCancel")}
            </Button>
            <Button
              size="compact-xs"
              color="red"
              loading={loading}
              onClick={async () => {
                const ok = await deleteDocPabean(item.id);
                if (ok) setOpened(false);
              }}
            >
              {t("docpabean.delete")}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Restore icon with an inline confirm popover, shown in place of the trash
 * icon for rows that are already soft-deleted. Clears deleted_at server-side
 * so the document returns to the list; the CEISA filing was never affected.
 */
function RestoreIconButton({ item }: { item: DocPabean }) {
  const [opened, setOpened] = useState(false);
  const { t } = useLanguage();
  const { restoreDocPabean, loading } = useRestoreDocPabean();

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md" width={240}>
      <Popover.Target>
        <Tooltip label={t("docpabean.tooltipRestore")}>
          <ActionIcon variant="subtle" onClick={() => setOpened((o) => !o)} style={{ color: "#2f9e44" }}>
            {loading ? <Loader size="xs" /> : <IconRestore size={18} />}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <MantineText size="sm">
            {t("docpabean.restoreConfirm").replace("{nomor}", item.nomorAju)}
          </MantineText>
          <Group justify="flex-end" gap="xs">
            <Button size="compact-xs" variant="default" onClick={() => setOpened(false)}>
              {t("docpabean.deleteCancel")}
            </Button>
            <Button
              size="compact-xs"
              color="green"
              loading={loading}
              onClick={async () => {
                const ok = await restoreDocPabean(item.id);
                if (ok) setOpened(false);
              }}
            >
              {t("docpabean.restore")}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Mobile-only card list. Phone-sized viewports can't fit the full
 * doc-pabean DataTable, so we stack one card per row with the same
 * action set the desktop Aksi column carries (View, Copy, Upload).
 */
/**
 * Parts of a page the API could not load (`incomplete`, backend#1916), and the
 * column titles each one covers. A failed part must never look like a real
 * value or a real empty value, so its cells show FailedCell instead.
 */
const INCOMPLETE_PART_TITLE_KEYS: Record<string, string[]> = {
  shipperBuyer: ["docpabean.colShipper", "docpabean.colBuyer"],
  type: ["docpabean.colType", "docpabean.colJenisKategori"],
  vessel: ["docpabean.colVesselVoy", "docpabean.colTglTibaEkspor"],
  totalItem: ["docpabean.colTotalItem"],
  status: ["docpabean.colStatus"],
  approval: ["docpabean.colApproval"],
  files: ["docpabean.incompleteFiles"],
};

function FailedCell({ themeColors, compact = false }: { themeColors: any; compact?: boolean }) {
  const { t } = useLanguage();
  return (
    <Tooltip label={t("docpabean.incompleteCellTooltip")} withArrow position="top" multiline maw={260}>
      <span
        data-testid="doc-pabean-failed-cell"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, color: themeColors.textSecondary }}
      >
        <IconAlertTriangle size={14} color={themeColors.warning || "#F59E0B"} />
        {!compact && (
          <MantineText size="xs" c="dimmed" component="span">
            {t("docpabean.incompleteCell")}
          </MantineText>
        )}
      </span>
    </Tooltip>
  );
}

function IncompleteNotice({
  parts,
  onReload,
  reloading,
}: {
  parts: Set<string>;
  onReload?: () => void;
  reloading?: boolean;
}) {
  const { t } = useLanguage();
  if (parts.size === 0) return null;
  const titles: string[] = [];
  for (const [part, keys] of Object.entries(INCOMPLETE_PART_TITLE_KEYS)) {
    if (!parts.has(part)) continue;
    for (const k of keys) titles.push(t(k));
  }
  if (titles.length === 0) return null;
  return (
    <Alert
      data-testid="doc-pabean-incomplete-notice"
      variant="light"
      color="yellow"
      radius="md"
      mb="sm"
      icon={<IconAlertTriangle size={18} />}
      title={t("docpabean.incompleteTitle")}
    >
      <Group justify="space-between" gap="sm" wrap="wrap">
        <MantineText size="sm">
          {t("docpabean.incompleteBody").replace("{list}", titles.join(", "))}
        </MantineText>
        {/* Solid, with autoContrast picking dark text: a light button on
            the light-yellow notice was nearly invisible (cream theme). */}
        {onReload && (
          <Button
            size="xs"
            variant="filled"
            color="yellow"
            autoContrast
            leftSection={<IconRefresh size={14} />}
            loading={reloading}
            onClick={onReload}
          >
            {t("docpabean.incompleteReload")}
          </Button>
        )}
      </Group>
    </Alert>
  );
}

function DocPabeanMobileCards({
  records,
  loading,
  themeColors,
  copyLoading,
  page,
  pageSize,
  totalRecords,
  onPageChange,
  onView,
  onCopy,
  onUpload,
  onPreviewPdf,
  onSyncRespon,
  syncResponLoadingAju,
  showFlags,
  failedParts,
}: {
  records: DocPabean[];
  loading?: boolean;
  themeColors: any;
  copyLoading: boolean;
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onView: (item: DocPabean) => void;
  onCopy: (item: DocPabean) => void;
  onUpload: (item: DocPabean) => void;
  onPreviewPdf: (item: DocPabean) => void;
  onSyncRespon: (item: DocPabean) => void;
  syncResponLoadingAju: string | null;
  /** The Kolom picker's flag switch: off hides the card's flag + ISO2. */
  showFlags: boolean;
  /** Parts the API could not load; their lines show "Gagal dimuat". */
  failedParts: Set<string>;
}) {
  const { t } = useLanguage();
  const lastPage = Math.max(1, Math.ceil(totalRecords / Math.max(1, pageSize)));
  const formatNomor = (value: string) => {
    if (!value) return "-";
    const raw = value.replace(/-/g, "");
    if (raw.length <= 6) return raw;
    if (raw.length <= 12) return `${raw.slice(0, 6)}-${raw.slice(6)}`;
    if (raw.length <= 20) return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12)}`;
    return `${raw.slice(0, 6)}-${raw.slice(6, 12)}-${raw.slice(12, 20)}-${raw.slice(20)}`;
  };

  if (loading && records.length === 0) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <MantineText size="sm" c="dimmed">{t("docpabean.loadingData")}</MantineText>
      </Group>
    );
  }
  if (records.length === 0) {
    return (
      <MantineText ta="center" size="sm" c="dimmed" py="xl">
        {t("docpabean.emptyFilter")}
      </MantineText>
    );
  }

  return (
    <Stack gap="sm" p="xs">
      {records.map((item) => {
        const isExport = item.nomorAju.startsWith("000030");
        const docTypes = new Set(
          (item.files || [])
            .map((f) => String(f.documentType || "").toUpperCase().trim())
            .filter(Boolean)
        );
        const hasInvoice = docTypes.has("380") || docTypes.has("I");
        const hasBl = docTypes.has("705") || docTypes.has("704") || docTypes.has("BL") || docTypes.has("MBL");
        const hasAwb = docTypes.has("740") || docTypes.has("741") || docTypes.has("AWB") || docTypes.has("MAWB");
        const isVerified = hasInvoice && (hasBl || hasAwb);
        const directionLabel = isExport ? "Export" : "Import";
        // A part that failed to load shows nothing rather than a guess.
        const mot = failedParts.has("type") ? null : resolveMot(item.motType);
        // The flag switch hides the card's flag + ISO2 row (the MOT chip stays).
        const iso2 = showFlags && !failedParts.has("shipperBuyer") ? toFlagCountryCode(item.shipperConsigneeCountry) : "";
        const flagSrc = flagAssetUrl(iso2);
        const vesselDate = vesselDateSummary(item);

        return (
          <div
            key={item.batchUUID}
            style={{
              borderRadius: 10,
              border: `1px solid ${themeColors.border}`,
              backgroundColor: themeColors.card,
              padding: 12,
              boxShadow: `0 1px 3px ${themeColors.border}40`,
            }}
          >
            <Group justify="space-between" wrap="nowrap" align="flex-start" mb={6}>
              <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                <MantineText fw={700} size="sm" style={{ color: themeColors.text, wordBreak: "break-word" }}>
                  {formatNomor(item.nomorAju)}
                </MantineText>
                {isVerified && (
                  <Tooltip label={t("docpabean.tooltipVerified")} withArrow>
                    <span style={{ display: "inline-flex", color: themeColors.textSecondary }}>
                      <IconCircleCheck size={14} />
                    </span>
                  </Tooltip>
                )}
              </Group>
              <Badge
                size="xs"
                radius="xs"
                variant="outline"
                styles={{
                  root: getShipmentTypeChipStyle(isExport, themeColors),
                }}
              >
                {directionLabel}
              </Badge>
            </Group>

            <Stack gap={2} mb={8}>
              <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                <strong style={{ color: themeColors.text }}>{t("docpabean.company")}:</strong> {item.namaPerusahaan || "-"}
              </MantineText>
              {item.namaProses && (
                <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                  <strong style={{ color: themeColors.text }}>{t("docpabean.proses")}:</strong> {item.namaProses}
                </MantineText>
              )}
              {item.drafterName && (
                <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                  <strong style={{ color: themeColors.text }}>{t("docpabean.colDrafter")}:</strong> {item.drafterName}
                </MantineText>
              )}
              <MantineText size="xs" style={{ color: themeColors.textSecondary }}>
                <strong style={{ color: themeColors.text }}>{t("docpabean.totalItemLabel")}:</strong>{" "}
                {failedParts.has("totalItem") ? <FailedCell themeColors={themeColors} /> : item.totalItem ?? 0}
              </MantineText>
              {failedParts.has("vessel") ? (
                <MantineText size="xs" component="div" style={{ color: themeColors.textSecondary }}>
                  <strong style={{ color: themeColors.text }}>{t("docpabean.cardVessel")}:</strong>{" "}
                  <FailedCell themeColors={themeColors} />
                </MantineText>
              ) : vesselDate && (
                <MantineText size="xs" data-testid="doc-pabean-card-vessel" style={{ color: themeColors.textSecondary }}>
                  <strong style={{ color: themeColors.text }}>{t("docpabean.cardVessel")}:</strong> {vesselDate.vessel || "-"}
                  {" · "}
                  <strong style={{ color: themeColors.text }}>{t("docpabean.cardTibaEkspor")}:</strong> {vesselDate.date || "-"}
                </MantineText>
              )}
            </Stack>

            {/* Country and MOT as the same two chips the desktop row uses, so the
                two views read as one design. No flag wash here — a 12px-padded
                card at phone width has no left margin to spend on it. */}
            {(iso2 || mot) && (
              <Group gap={6} wrap="nowrap" mb={8}>
                {iso2 && (
                  <Group gap={4} wrap="nowrap">
                    {flagSrc && (
                      <img
                        src={flagSrc}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 16,
                          height: 12,
                          objectFit: "cover",
                          borderRadius: 2,
                          boxShadow: `inset 0 0 0 1px ${themeColors.border}`,
                          display: "block",
                        }}
                      />
                    )}
                    <MantineText size="10px" fw={600} style={{ color: themeColors.textSecondary, letterSpacing: 0.3 }}>
                      {iso2}
                    </MantineText>
                  </Group>
                )}
                {mot && <MotChip mot={mot} themeColors={themeColors} />}
              </Group>
            )}

            <Group justify="flex-end" gap={4} wrap="nowrap">
              <Tooltip label={t("docpabean.tooltipViewEdit")}>
                <ActionIcon variant="subtle" size="md" onClick={() => onView(item)} styles={getHoverActionIconStyles(themeColors, themeColors.primary)}>
                  <IconEye size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t(item.nomorAju ? "docpabean.tooltipPreviewPdf" : "docpabean.tooltipPreviewPdfNoAju")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={() => onPreviewPdf(item)}
                  disabled={!item.nomorAju}
                  styles={getHoverActionIconStyles(themeColors, themeColors.purple)}
                >
                  <IconFileTypePdf size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t("docpabean.tooltipCopy")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={() => onCopy(item)}
                  disabled={copyLoading}
                  styles={getHoverActionIconStyles(themeColors, themeColors.green)}
                >
                  {copyLoading ? <Loader size="xs" /> : <IconCopy size={18} />}
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t("docpabean.tooltipUpload")}>
                <ActionIcon variant="subtle" size="md" onClick={() => onUpload(item)} styles={getHoverActionIconStyles(themeColors, themeColors.accent)}>
                  <IconUpload size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={t("docpabean.tooltipSyncRespon")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={() => onSyncRespon(item)}
                  disabled={syncResponLoadingAju === item.nomorAju}
                  styles={getHoverActionIconStyles(themeColors, themeColors.warning)}
                >
                  {syncResponLoadingAju === item.nomorAju ? <Loader size="xs" /> : <IconRefresh size={18} />}
                </ActionIcon>
              </Tooltip>
              {item.isDeleted ? <RestoreIconButton item={item} /> : <DeleteIconButton item={item} themeColors={themeColors} />}
            </Group>
          </div>
        );
      })}

      {/* Pagination footer */}
      <Group justify="space-between" align="center" mt="xs" px="xs">
        <MantineText size="xs" c="dimmed">
          {t("docpabean.page")} {page} / {lastPage} · {totalRecords} total
        </MantineText>
        <Group gap={6}>
          <ActionIcon
            variant="default"
            size="md"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            ‹
          </ActionIcon>
          <ActionIcon
            variant="default"
            size="md"
            onClick={() => onPageChange(Math.min(lastPage, page + 1))}
            disabled={page >= lastPage}
          >
            ›
          </ActionIcon>
        </Group>
      </Group>
    </Stack>
  );
}

export default function DocPabeanTable({
  data,
  numberPageSize,
  numberPage,
  loading = false,
  setNumberPageSize,
  onSearch,
  totalPage = 0,
  onPageChange,
  pageKey = "doc-pabean-table",
  incomplete,
  onReload,
}: DocPabeanTableProps) {
  const { colors: themeColors } = useTheme();
  const { t } = useLanguage();
  const failedParts = useMemo(() => new Set(incomplete ?? []), [incomplete]);
  // Kolom picker choices for this page (same storage the toolbar picker writes).
  const { columnsToggle } = useDocPabeanColumnToggle(pageKey);
  const { prefs: displayPrefs } = useDocPabeanDisplayPrefs(pageKey);
  const [pageSize, setPageSize] = useState(numberPageSize);
  const [records, setRecords] = useState<DocPabean[]>(data.slice(0, pageSize));
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "nomorAju",
    direction: "asc",
  });
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 200);

  // Unggah Berkas modal state — keyed by batchUUID since the modal fetches
  // the per-batch CEISA edit-document list from the backend.
  const [unggahBerkasOpen, setUnggahBerkasOpen] = useState(false);
  const [unggahBerkasBatchUUID, setUnggahBerkasBatchUUID] = useState("");
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewAju, setPdfPreviewAju] = useState<string | null>(null);
  const openPdfPreview = (item: DocPabean) => {
    if (!item.nomorAju) return;
    setPdfPreviewAju(item.nomorAju);
    setPdfPreviewOpen(true);
  };
  const [filters, setFilters] = useState({
    shipmentType: '',
    namaPerusahaan: '',
    drafterName: '',
    motType: '',
    shipperConsigneeCountry: ''
  });
  const { copyDocPabean, loading: copyLoading } = useCopyDocPabean();
  const { syncRespon, loadingAju: syncResponLoadingAju } = useSyncRespon();
  const isFirstRender = useRef(true);
  // Mobile flag — render a card stack instead of the wide DataTable when
  // the viewport is too narrow for the column set.
  const isMobile = useMediaQuery("(max-width: 768px)");

  const filteredRecords = records.filter((item) => {
    const matchesShipmentType = !filters.shipmentType || 
      (item.nomorAju.startsWith('000030') ? 'export' : 'import') === filters.shipmentType;
    
    const matchesNamaPerusahaan = !filters.namaPerusahaan ||
      (item.senderName || item.namaPerusahaan)?.toLowerCase().includes(filters.namaPerusahaan.toLowerCase());
    
    const matchesDrafterName = !filters.drafterName || 
      item.drafterName?.toLowerCase().includes(filters.drafterName.toLowerCase());
    
    // resolveMot, not a substring test: "LAUT" is a sea filing and an empty
    // motType is not an air one.
    const matchesMotType = !filters.motType ||
      resolveMot(item.motType) === filters.motType;
    
    const matchesCountry = !filters.shipperConsigneeCountry || 
      item.shipperConsigneeCountry?.toLowerCase().includes(filters.shipperConsigneeCountry.toLowerCase());

    return matchesShipmentType && matchesNamaPerusahaan && 
           matchesDrafterName && matchesMotType && matchesCountry;
  });

  const filterControls = (
    <Group mb="md" grow className="jv-filterbar">
      <Select
        placeholder={t("docpabean.filterShipmentType")}
        value={filters.shipmentType}
        onChange={(value) => setFilters(prev => ({ ...prev, shipmentType: value || '' }))}
        data={[
          { value: 'export', label: t("docpabean.export") },
          { value: 'import', label: t("docpabean.import") }
        ]}
        clearable
        styles={{
          input: {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            borderColor: themeColors.inputBorder,
            '::placeholder': {
              color: themeColors.inputPlaceholder,
            }
          },
          dropdown: {
            backgroundColor: themeColors.dropdownBackground,
            borderColor: themeColors.dropdownBorder,
          },
          option: {
            color: themeColors.dropdownText,
            '&:hover': {
              backgroundColor: themeColors.dropdownHover,
            }
          }
        }}
      />
      <TextInput
        placeholder={t("docpabean.filterNamaPerusahaan")}
        value={filters.namaPerusahaan}
        onChange={(e) => setFilters(prev => ({ ...prev, namaPerusahaan: e.target.value }))}
        styles={{
          input: {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            borderColor: themeColors.inputBorder,
            '::placeholder': {
              color: themeColors.inputPlaceholder,
            }
          }
        }}
      />
      <TextInput
        placeholder={t("docpabean.filterDrafter")}
        value={filters.drafterName}
        onChange={(e) => setFilters(prev => ({ ...prev, drafterName: e.target.value }))}
        styles={{
          input: {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            borderColor: themeColors.inputBorder,
            '::placeholder': {
              color: themeColors.inputPlaceholder,
            }
          }
        }}
      />
      <Select
        placeholder={t("docpabean.filterMotType")}
        value={filters.motType}
        onChange={(value) => setFilters(prev => ({ ...prev, motType: value || '' }))}
        data={[
          { value: 'SEA', label: 'Sea' },
          { value: 'AIR', label: 'Air' }
        ]}
        clearable
        styles={{
          input: {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            borderColor: themeColors.inputBorder,
            '::placeholder': {
              color: themeColors.inputPlaceholder,
            }
          },
          dropdown: {
            backgroundColor: themeColors.dropdownBackground,
            borderColor: themeColors.dropdownBorder,
          },
          option: {
            color: themeColors.dropdownText,
            '&:hover': {
              backgroundColor: themeColors.dropdownHover,
            }
          }
        }}
      />
      <TextInput
        placeholder={t("docpabean.filterCountry")}
        value={filters.shipperConsigneeCountry}
        onChange={(e) => setFilters(prev => ({ ...prev, shipperConsigneeCountry: e.target.value }))}
        styles={{
          input: {
            backgroundColor: themeColors.inputBackground,
            color: themeColors.inputText,
            borderColor: themeColors.inputBorder,
            '::placeholder': {
              color: themeColors.inputPlaceholder,
            }
          }
        }}
      />
    </Group>
  );

  const allColumns: DataTableProps<DocPabean>["columns"] = [
    {
      accessor: "nomorAju",
      title: t("docpabean.colNomorAju"),
      textAlign: "center",
      width: 250,
      render: (item) => {
        const formatNomorAju = (value: string) => {
          if (!value) return "-";
          // Remove any existing dashes to get the raw value
          const rawValue = value.replace(/-/g, '');
          // Format: 6-6-8-6 characters with dashes
          let formatted = rawValue;
          if (rawValue.length > 6) {
            formatted = rawValue.slice(0, 6) + '-' + rawValue.slice(6);
          }
          if (rawValue.length > 12) {
            formatted = rawValue.slice(0, 6) + '-' + rawValue.slice(6, 12) + '-' + rawValue.slice(12);
          }
          if (rawValue.length > 20) {
            formatted = rawValue.slice(0, 6) + '-' + rawValue.slice(6, 12) + '-' + rawValue.slice(12, 20) + '-' + rawValue.slice(20);
          }
          return formatted;
        };

        // Verified-bundle check: row needs Invoice (380) + BL/MBL/AWB/MAWB.
        // Codes line up with the values used everywhere else in the editor
        // (DokumenForm submit gate, BasicInfoTab, etc.).
        const docTypes = new Set(
          (item.files || [])
            .map((f) => String(f.documentType || "").toUpperCase().trim())
            .filter(Boolean)
        );
        const hasInvoice = docTypes.has("380") || docTypes.has("I");
        const hasBl = docTypes.has("705") || docTypes.has("704") || docTypes.has("BL") || docTypes.has("MBL");
        const hasAwb = docTypes.has("740") || docTypes.has("741") || docTypes.has("AWB") || docTypes.has("MAWB");
        const isVerified = hasInvoice && (hasBl || hasAwb);

        return (
          <Group gap={6} justify="center" wrap="nowrap">
            <MantineText ta="center" size="xs" c={themeColors.tableText}>
              {formatNomorAju(item.nomorAju)}
            </MantineText>
            {/* Files failed to load: the verified check would be a guess. */}
            {failedParts.has("files") && <FailedCell themeColors={themeColors} compact />}
            {!failedParts.has("files") && isVerified && (
              <Tooltip
                label={t("docpabean.tooltipVerified")}
                withArrow
                position="top"
              >
                <span
                  aria-label="Verified bundle"
                  style={{ display: "inline-flex", alignItems: "center", color: themeColors.textSecondary }}
                >
                  <IconCircleCheck size={16} />
                </span>
              </Tooltip>
            )}
          </Group>
        );
      },
    },
    {
      accessor: "shipmentType",
      title: t("docpabean.colType"),
      textAlign: "center",
      // Second column, not buried mid-table: direction and mode are what the
      // row is scanned for, and the old Mot column sat past the horizontal fold
      // at 1440px, so its icon was a scroll away. Reading it here costs nothing.
      width: 140,
      render: (item) => {
        const isExport = item.nomorAju.startsWith("000030");
        const label = isExport ? "E" : "I";
        const mot = resolveMot(item.motType);
        // Side by side, not stacked: a second line here would push the cell past
        // the table's 50px row height.
        return (
          <Group gap={6} justify="center" wrap="nowrap">
            <span
              className="inline-block px-1.5 py-0.5 text-[11px] font-semibold tracking-[0.3px] text-center"
              title={isExport ? t("docpabean.export") : t("docpabean.import")}
              style={getShipmentTypeChipStyle(isExport, themeColors)}
            >
              {label}
            </span>
            {failedParts.has("type") && <FailedCell themeColors={themeColors} compact />}
            {/* No chip at all when the filing never recorded a mode — better a
                gap than a confident wrong icon. */}
            {!failedParts.has("type") && mot && (
              <Tooltip
                label={mot === "SEA" ? t("docpabean.motSea") : t("docpabean.motAir")}
                withArrow
                position="top"
              >
                <span style={{ display: "inline-flex" }}>
                  <MotChip mot={mot} themeColors={themeColors} />
                </span>
              </Tooltip>
            )}
          </Group>
        );
      },
    },
    {
      accessor: "namaPerusahaan",
      title: t("docpabean.colShipper"),
      // Left-aligned, unlike its neighbours: the logo well leads the cell, and
      // centring would leave the badges ragged down the column.
      textAlign: "left",
      // Widened from 250 to absorb the country chip folded in from the removed
      // Country column, using room the two deleted columns just freed.
      width: 300,
      // While shipper/buyer failed to load, never fall back to the PPJK's
      // company name: that would show a wrong name as the shipper.
      render: (item) => failedParts.has("shipperBuyer") ? <FailedCell themeColors={themeColors} /> : (
        <CompanyNameCell
          name={item.senderName || item.namaPerusahaan}
          logoUrl={item.shipperLogoUrl}
          logoOnDark={item.shipperLogoOnDark}
          website={item.shipperWebsite}
          country={item.shipperConsigneeCountry}
          withLogo={displayPrefs.logos}
          withFlag={displayPrefs.flags}
          themeColors={themeColors}
        />
      ),
    },
    {
      accessor: "buyerName",
      title: t("docpabean.colBuyer"),
      textAlign: "left",
      width: 180,
      render: (item) => failedParts.has("shipperBuyer") ? <FailedCell themeColors={themeColors} /> : (
        <CompanyNameCell
          name={item.buyerName}
          logoUrl={item.buyerLogoUrl}
          logoOnDark={item.buyerLogoOnDark}
          website={item.buyerWebsite}
          withLogo={displayPrefs.logos}
          themeColors={themeColors}
        />
      ),
    },
    {
      accessor: "vesselVoy",
      title: t("docpabean.colVesselVoy"),
      textAlign: "left",
      width: 180,
      render: (item) => {
        if (failedParts.has("vessel")) return <FailedCell themeColors={themeColors} />;
        const name = cleanText(item.namaPengangkut);
        const voy = cleanText(item.nomorPengangkut);
        if (!name && !voy) {
          return (
            <MantineText ta="center" size="xs" c="dimmed">
              -
            </MantineText>
          );
        }
        const full = [name, voy].filter(Boolean).join(" / ");
        // Two lines at most so the row keeps its 50px height: the name may wrap
        // to two lines only when there is no voyage line under it.
        return (
          <Tooltip label={full} withArrow position="top" openDelay={300} multiline maw={320}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <MantineText size="xs" c={themeColors.tableText} lineClamp={voy ? 1 : 2} style={{ wordBreak: "break-word" }}>
                {name || "-"}
              </MantineText>
              {voy && (
                <MantineText size="xs" c="dimmed" lineClamp={1} style={{ fontSize: "10px" }}>
                  {voy}
                </MantineText>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      accessor: "tanggalTibaEkspor",
      title: t("docpabean.colTglTibaEkspor"),
      textAlign: "center",
      // Fits "Tanggal ETA / ETD" without truncating (120 cut it to "Tanggal ETA / …").
      width: 170,
      render: (item) => {
        if (failedParts.has("vessel")) return <FailedCell themeColors={themeColors} />;
        const iso = cleanText(item.tanggalTibaEkspor);
        const raw = iso ? null : cleanText(item.tanggalTibaEksporRaw);
        if (!iso && !raw) {
          return (
            <MantineText ta="center" size="xs" c="dimmed">
              -
            </MantineText>
          );
        }
        const isExport = item.nomorAju.startsWith("000030");
        return (
          <Tooltip
            label={t(isExport ? "docpabean.tooltipTglEkspor" : "docpabean.tooltipTglTiba")}
            withArrow
            position="top"
          >
            <MantineText ta="center" size="xs" c={iso ? themeColors.tableText : "dimmed"} lineClamp={2}>
              {iso || raw}
            </MantineText>
          </Tooltip>
        );
      },
    },
    {
      accessor: "tanggalDaftar",
      title: t("docpabean.colDate"),
      textAlign: "center",
      render: (item) => {
        const time = getTimeFromCreatedAt(item.createdAt);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <MantineText ta="center" size="xs" c={themeColors.tableText}>
              {item.tanggalDaftar || "-"}
            </MantineText>
            {time && (
              <MantineText ta="center" size="xs" c="dimmed" style={{ fontSize: '10px' }}>
                {time}
              </MantineText>
            )}
          </div>
        );
      },
    },
    {
      accessor: "respon",
      title: t("docpabean.colRespon"),
      textAlign: "center",
      width: 180,
      render: (item) => (
        <StatusChip status={item.namaProses} />
      ),
    },
    {
      accessor: "status",
      title: t("docpabean.colStatus"),
      textAlign: "center",
      width: 200,
      render: (item) => {
        if (failedParts.has("status")) return <FailedCell themeColors={themeColors} />;
        const firstStatus = item.statuses?.[0];

        // Deleted in Jarvis only — the filing at CEISA is untouched. Shown
        // here rather than beside the nomor aju, which it crowded into
        // wrapping; a deleted document can still carry CEISA statuses, so it
        // stacks above them rather than replacing them.
        const deletedBadge = item.isDeleted ? (
          <Badge size="xs" color="red" variant="light">
            {t("docpabean.deletedBadge")}
          </Badge>
        ) : null;

        if (!firstStatus) {
          return (
            <Group justify="center" gap={4}>
              {deletedBadge ?? (
                <MantineText ta="center" size="xs" c="dimmed">
                  -
                </MantineText>
              )}
            </Group>
          );
        }
        return (
          <Stack gap={4} align="center">
            {deletedBadge}
            <StatusCellWithInfo
              statuses={item.statuses || []}
              themeColors={themeColors}
            />
          </Stack>
        );
      },
    },
    {
      accessor: "jenisKategori",
      title: t("docpabean.colJenisKategori"),
      textAlign: "center",
      width: 180,
      render: (item) => {
        if (failedParts.has("type")) return <FailedCell themeColors={themeColors} />;
        const jenis = (item.jenisEkspor || item.jenisImpor || "").toString().toUpperCase();
        const kategori = (item.kategoriEkspor || item.kategoriImpor || "").toString().toUpperCase();
        if (!jenis && !kategori) {
          return (
            <MantineText ta="center" size="xs" c="dimmed">
              -
            </MantineText>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            {jenis && (
              <span
                style={{
                  display: 'inline-block',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '1px 6px',
                  borderRadius: 4,
                  backgroundColor: themeColors.formFields,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                }}
              >
                {jenis}
              </span>
            )}
            {kategori && (
              <span
                style={{
                  display: 'inline-block',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '1px 6px',
                  borderRadius: 4,
                  backgroundColor: themeColors.formFields,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                }}
              >
                {kategori}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessor: "drafter",
      title: t("docpabean.colDrafter"),
      textAlign: "center",
      render: (item) => {
        return (
          <MantineText ta="center" size="xs" c={themeColors.tableText}>
            {item.drafterName || "-"}
          </MantineText>
        );
      },
    },
    {
      accessor: "totalItem",
      title: t("docpabean.colTotalItem"),
      textAlign: "center",
      render: (item) => failedParts.has("totalItem") ? <FailedCell themeColors={themeColors} /> : (
        <MantineText ta="center" size="xs" c={themeColors.tableText}>
          {item.totalItem || "-"}
        </MantineText>
      ),
    },
    {
      accessor: "approvalStatus",
      title: t("docpabean.colApproval"),
      textAlign: "center",
      width: 140,
      render: (item) => {
        if (failedParts.has("approval")) return <FailedCell themeColors={themeColors} />;
        if (!item.approvalStatus) {
          return (
            <MantineText ta="center" size="xs" c="dimmed">
              -
            </MantineText>
          );
        }
        const approvalLabelKeys: Record<string, string> = {
          APPROVED: 'docpabean.approved',
          WAITING_FOR_REVIEW: 'docpabean.waiting',
          NEED_REVISION: 'docpabean.needRevision',
          REJECTED: 'docpabean.rejected',
          CANCELLED: 'docpabean.cancelled',
          SCHEDULED: 'docpabean.scheduled',
        };
        const label = t(approvalLabelKeys[item.approvalStatus] || `docpabean.approved`);
        const relative = formatRelativeTime(item.approvalDate, t);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span
              style={{
                display: 'inline-block',
                fontSize: 11,
                fontWeight: 500,
                padding: '1px 6px',
                borderRadius: 4,
                backgroundColor: themeColors.formFields,
                color: themeColors.text,
                border: `1px solid ${themeColors.border}`,
                whiteSpace: 'nowrap',
              }}
            >
              {label ?? item.approvalStatus}
            </span>
            {relative && (
              <MantineText ta="center" size="xs" c="dimmed" style={{ fontSize: 10 }}>
                {relative}
              </MantineText>
            )}
          </div>
        );
      },
    },
    {
      accessor: "actions",
      title: t("docpabean.colActions"),
      textAlign: "center",
      // Seven icons need ~210px; the extra room is breathing space either side.
      width: 248,
      cellsStyle: () => ({ paddingInline: 16 }),
      render: (item) => {
        const isImport = !item.nomorAju.startsWith("000030");
        const isExport = item.nomorAju.startsWith("000030");
        const shipmentType = isImport ? "import" : "export";
        
        const handleCopy = async () => {
          // Must open before await — see navigateCopiedDocument.
          const newTab = window.open("about:blank", "_blank");
          try {
            const batchUUID = await copyDocPabean(item.batchUUID, shipmentType);
            if (!batchUUID) {
              newTab?.close();
              return;
            }

            const targetUrl = isImport
              ? `/doc-pabean/draft/${batchUUID}/edit?fromCopy=true`
              : `/export?batchUUID=${batchUUID}&fromCopy=true`;

            navigateCopiedDocument(targetUrl, newTab);
          } catch (error) {
            newTab?.close();
            console.error('Copy doc pabean failed:', error);
          }
        };

        // Determine access level based on user email
        // Listed users get their defined level; unlisted molgroup users default to 'view'; everyone else gets 'edit'
        const userEmail = getUserEmail();
        const isMolgroup = userEmail.endsWith('@molgroup.com');
        const importAccessLevel = BC20_ACCESS_RULES[userEmail] || (isMolgroup ? 'view' : 'edit');
        const exportAccessLevel = BC30_ACCESS_RULES[userEmail] || (isMolgroup ? 'view' : 'edit');
        const isViewOnly = isImport
          ? importAccessLevel === 'view'
          : exportAccessLevel === 'view';

        const handleEdit = () => {
          if (isImport) {
            const query = buildDraftEditQuery({
              isViewOnly,
              respon: item.namaProses,
            });
            window.open(`/doc-pabean/draft/${item.batchUUID}/edit${query}`, '_blank');
          } else {
            const viewOnlyParam = isViewOnly ? '&isViewOnly=true' : '';
            window.open(`/export?batchUUID=${item.batchUUID}${viewOnlyParam}`, '_blank');
          }
        };

        return (
          <Group gap={4} justify="center" wrap="nowrap">
            <StatusHistoryIconButton
              ajuNo={item.nomorAju}
              totalItem={item.totalItem}
              shipperCountry={item.shipperConsigneeCountry}
            />

            {isViewOnly ? (
              <Tooltip label={t("docpabean.tooltipView")}>
                <ActionIcon
                  variant="subtle"
                  onClick={handleEdit}
                  styles={getHoverActionIconStyles(themeColors, themeColors.primary)}
                >
                  <IconEye size={18} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label={t("docpabean.tooltipEdit")}>
                <ActionIcon
                  variant="subtle"
                  onClick={handleEdit}
                  styles={getHoverActionIconStyles(themeColors, themeColors.primary)}
                >
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>
            )}

            {/* Read-only, so view-only users get it too. */}
            <Tooltip label={t(item.nomorAju ? "docpabean.tooltipPreviewPdf" : "docpabean.tooltipPreviewPdfNoAju")}>
              <ActionIcon
                variant="subtle"
                onClick={() => openPdfPreview(item)}
                disabled={!item.nomorAju}
                styles={getHoverActionIconStyles(themeColors, themeColors.purple)}
              >
                <IconFileTypePdf size={18} />
              </ActionIcon>
            </Tooltip>

            {!isViewOnly && (
              <Tooltip label={t("docpabean.tooltipCopy")}>
                <ActionIcon
                  variant="subtle"
                  onClick={handleCopy}
                  disabled={copyLoading}
                  styles={getHoverActionIconStyles(themeColors, themeColors.green)}
                >
                  {copyLoading ? <Loader size="xs" /> : <IconCopy size={18} />}
                </ActionIcon>
              </Tooltip>
            )}

            <Tooltip label={t("docpabean.tooltipUpload")}>
              <ActionIcon
                variant="subtle"
                onClick={() => {
                  setUnggahBerkasBatchUUID(item.batchUUID);
                  setUnggahBerkasOpen(true);
                }}
                  styles={getHoverActionIconStyles(themeColors, themeColors.accent)}
              >
                <IconUpload size={18} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label={t("docpabean.tooltipSyncRespon")}>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => {
                  void syncRespon(item.nomorAju);
                }}
                disabled={syncResponLoadingAju === item.nomorAju}
                styles={getHoverActionIconStyles(themeColors, themeColors.warning)}
              >
                {syncResponLoadingAju === item.nomorAju ? (
                  <Loader size="xs" />
                ) : (
                  <IconRefresh size={16} />
                )}
              </ActionIcon>
            </Tooltip>

            {!isViewOnly &&
              (item.isDeleted ? <RestoreIconButton item={item} /> : <DeleteIconButton item={item} themeColors={themeColors} />)}
          </Group>
        );
      },
    },
  ];

  // Code order and widths; only visibility comes from the Kolom picker.
  const columns = allColumns.map((column) => ({
    ...column,
    hidden: isColumnHidden(String(column.accessor), columnsToggle),
  }));

  // Sync local pageSize with prop when it changes externally
  useEffect(() => {
    if (numberPageSize !== pageSize) {
      setPageSize(numberPageSize);
    }
  }, [numberPageSize]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Only reset to page 1 when pageSize actually changes (not on initial render)
    onPageChange?.(1);
    setNumberPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    setRecords(data);
  }, [data, numberPage]);

  // Use theme colors for table styling - works with any theme
  const tableHeaderBg = themeColors.tableHeader;
  const tableRowBg = themeColors.tableRow;
  const tableRowAltBg = themeColors.tableRowAlt;
  const tableRowHover = themeColors.tableRowHover;
  const tableRowSelected = themeColors.tableRowSelected;

  return (
    <>
      {filterControls}
      <IncompleteNotice parts={failedParts} onReload={onReload} reloading={loading} />
      <Box pos="relative">
      <LoadingOverlay
        visible={copyLoading}
        zIndex={200}
        overlayProps={{ blur: 1 }}
        loaderProps={{ type: "bars" }}
      />
      <div style={{ border: `1px solid ${themeColors.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        <style>{`
          .doc-pabean-table .mantine-datatable-header {
            background: ${tableHeaderBg} !important;
            height: 51px !important;
          }
          .doc-pabean-table .mantine-datatable-header th {
            background: ${tableHeaderBg} !important;
            color: ${themeColors.tableHeaderText} !important;
            font-weight: 600 !important;
            font-size: 14px !important;
            text-align: center !important;
            border-bottom: 1px solid ${themeColors.border} !important;
            height: 50px !important;
            padding: 12px 8px !important;
          }
          .doc-pabean-table .mantine-datatable-row {
            height: 50px !important;
            border-bottom: 1px solid ${themeColors.border} !important;
            background-color: ${tableRowBg} !important;
          }
          .doc-pabean-table .mantine-datatable-row:nth-child(even) {
            background-color: ${tableRowAltBg} !important;
          }
          .doc-pabean-table .mantine-datatable-row:hover {
            background-color: ${tableRowHover} !important;
          }
          .doc-pabean-table .mantine-datatable-row td {
            border-right: 1px solid ${themeColors.border} !important;
            padding: 12px 8px !important;
            font-size: 12px !important;
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-datatable-row:last-child {
            border-bottom: none !important;
          }
          .doc-pabean-table .mantine-datatable-row.mantine-datatable-row--selected {
            background-color: ${tableRowSelected} !important;
          }
          .doc-pabean-table .mantine-datatable {
            border: none !important;
            background: ${themeColors.card} !important;
            color: ${themeColors.tableText} !important;
            --mantine-datatable-background-color: ${themeColors.card} !important;
            --mantine-datatable-background-color-light: ${themeColors.card} !important;
            --mantine-datatable-background-color-dark: ${themeColors.card} !important;
            --mantine-datatable-border-color: ${themeColors.border} !important;
            --mantine-datatable-border-color-light: ${themeColors.border} !important;
            --mantine-datatable-border-color-dark: ${themeColors.border} !important;
            --mantine-datatable-row-border-color: ${themeColors.border} !important;
            --mantine-datatable-row-border-color-light: ${themeColors.border} !important;
            --mantine-datatable-row-border-color-dark: ${themeColors.border} !important;
            --mantine-datatable-striped-color: ${tableRowAltBg} !important;
            --mantine-datatable-striped-color-light: ${tableRowAltBg} !important;
            --mantine-datatable-striped-color-dark: ${tableRowAltBg} !important;
            --mantine-datatable-highlight-on-hover-color: ${tableRowHover} !important;
            --mantine-datatable-highlight-on-hover-color-light: ${tableRowHover} !important;
            --mantine-datatable-highlight-on-hover-color-dark: ${tableRowHover} !important;
            --mantine-datatable-color: ${themeColors.tableText} !important;
            --mantine-datatable-color-light: ${themeColors.tableText} !important;
            --mantine-datatable-color-dark: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-datatable-table {
            border-bottom: none !important;
          }
          .doc-pabean-table .mantine-DataTable-footer {
            background: ${themeColors.tableFooter} !important;
            border-top: 1px solid ${themeColors.border} !important;
            color: ${themeColors.tableFooterText} !important;
            padding: 12px !important;
          }
          .doc-pabean-table .mantine-DataTable-footer * {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-DataTable-pagination {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-DataTable-paginationText {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="DataTable-footer"] {
            background: ${themeColors.tableFooter} !important;
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="DataTable-footer"] * {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="DataTable-paginationText"] {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-DataTable-footer .mantine-Text-root {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="paginationText"] {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="footer"] span {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table [class*="footer"] div {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-Pagination-control {
            background-color: ${themeColors.tableFooter} !important;
            color: ${themeColors.tableFooterText} !important;
            border-color: ${themeColors.border} !important;
          }
          .doc-pabean-table .mantine-Pagination-control:hover:not([data-disabled]) {
            background-color: ${tableRowHover} !important;
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-Pagination-control[data-active] {
            background-color: ${themeColors.accent} !important;
            color: #FFFFFF !important;
            border-color: ${themeColors.accent} !important;
          }
          .doc-pabean-table .mantine-Pagination-control[data-disabled] {
            background-color: ${themeColors.background} !important;
            color: ${themeColors.placeholder} !important;
            border-color: ${themeColors.border} !important;
          }
          .doc-pabean-table .mantine-Select-input,
          .doc-pabean-table .mantine-Input-input {
            background-color: ${themeColors.tableFooter} !important;
            color: ${themeColors.tableFooterText} !important;
            border-color: ${themeColors.border} !important;
          }
          .doc-pabean-table .mantine-datatable-footer-pagination-text {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-datatable-footer span,
          .doc-pabean-table .mantine-datatable-footer div {
            color: ${themeColors.tableFooterText} !important;
          }
          .doc-pabean-table .mantine-Select-dropdown {
            background-color: ${themeColors.dropdownBackground} !important;
            border-color: ${themeColors.dropdownBorder} !important;
          }
          .doc-pabean-table .mantine-Select-option {
            color: ${themeColors.dropdownText} !important;
            background-color: transparent !important;
          }
          .doc-pabean-table .mantine-Select-option:hover {
            background-color: ${themeColors.dropdownHover} !important;
          }
          .doc-pabean-table .mantine-datatable-pagination .mantine-Button-root {
            background-color: ${themeColors.formFields} !important;
            color: ${themeColors.tableFooterText} !important;
            border: 1px solid ${themeColors.border} !important;
          }
          .doc-pabean-table .mantine-datatable-pagination .mantine-Button-root:hover {
            background-color: ${tableRowHover} !important;
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-Menu-dropdown {
            background-color: ${themeColors.card} !important;
            border: 1px solid ${themeColors.border} !important;
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-Menu-item {
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-Menu-item[data-hovered="true"],
          .doc-pabean-table .mantine-Menu-item[data-active="true"] {
            background-color: ${tableRowHover} !important;
            color: ${themeColors.tableText} !important;
          }
          .doc-pabean-table .mantine-datatable-page-size-selector-menu-arrow {
            background-color: ${themeColors.card} !important;
            border: 1px solid ${themeColors.border} !important;
          }
        `}</style>
        <div className="doc-pabean-table">
          {isMobile ? (
            <DocPabeanMobileCards
              records={filteredRecords}
              loading={loading}
              themeColors={themeColors}
              copyLoading={copyLoading}
              page={numberPage}
              pageSize={pageSize}
              totalRecords={totalPage * numberPageSize}
              onPageChange={(p) => onPageChange?.(p)}
              onView={(item) => {
                const email = getUserEmail();
                const isMolgroup = email.endsWith("@molgroup.com");
                const isExport = item.nomorAju.startsWith("000030");
                const access = isExport
                  ? BC30_ACCESS_RULES[email] || (isMolgroup ? "view" : "edit")
                  : BC20_ACCESS_RULES[email] || (isMolgroup ? "view" : "edit");
                if (isExport) {
                  const viewOnlyParam = access === "view" ? "?isViewOnly=true" : "";
                  window.open(`/export?batchUUID=${item.batchUUID}${viewOnlyParam}`, "_blank");
                } else {
                  const query = buildDraftEditQuery({
                    isViewOnly: access === "view",
                    respon: item.namaProses,
                  });
                  window.open(`/doc-pabean/draft/${item.batchUUID}/edit${query}`, "_blank");
                }
              }}
              onCopy={async (item) => {
                const newTab = window.open("about:blank", "_blank");
                try {
                  const isExport = item.nomorAju.startsWith("000030");
                  const shipmentType = isExport ? "export" : "import";
                  const batchUUID = await copyDocPabean(item.batchUUID, shipmentType);
                  if (!batchUUID) {
                    newTab?.close();
                    return;
                  }
                  const targetUrl = !isExport
                    ? `/doc-pabean/draft/${batchUUID}/edit?fromCopy=true`
                    : `/export?batchUUID=${batchUUID}&fromCopy=true`;
                  navigateCopiedDocument(targetUrl, newTab);
                } catch (e) {
                  newTab?.close();
                  console.error("Copy failed", e);
                }
              }}
              onUpload={(item) => {
                setUnggahBerkasBatchUUID(item.batchUUID);
                setUnggahBerkasOpen(true);
              }}
              onPreviewPdf={openPdfPreview}
              onSyncRespon={(item) => {
                void syncRespon(item.nomorAju);
              }}
              syncResponLoadingAju={syncResponLoadingAju}
              showFlags={displayPrefs.flags}
              failedParts={failedParts}
            />
          ) : (
          <DataTable
            minHeight={200}
            verticalSpacing="xs"
            striped
            highlightOnHover
            columns={columns}
            records={filteredRecords}
            totalRecords={totalPage * numberPageSize}
            recordsPerPage={pageSize}
            page={numberPage}
            onPageChange={(p) => {
              onPageChange?.(p);
            }}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={(value) => {
              setPageSize(value);
              setNumberPageSize(value);
            }}
            fetching={loading}
            pinLastColumn={true}
            scrollAreaProps={{ type: 'hover' }}
            styles={{
              root: {
                backgroundColor: themeColors.card,
                color: themeColors.tableText,
              },
              table: {
                backgroundColor: themeColors.card,
                color: themeColors.tableText,
              },
              header: {
                backgroundColor: tableHeaderBg,
              },
              footer: {
                backgroundColor: themeColors.tableFooter,
                color: `${themeColors.tableFooterText} !important`,
                borderTop: `1px solid ${themeColors.border}`,
              },
              pagination: {
                color: `${themeColors.tableFooterText} !important`,
              }
            }}
            c={themeColors.tableText}
            paginationText={({ from, to, totalRecords }) => (
              <span style={{ color: themeColors.tableFooterText }}>
                {from} - {to} / {totalRecords}
              </span>
            )}
          />
          )}
        </div>
      </div>
      </Box>

      {/* Unggah Berkas Modal — per-batch edit-doc list with upload */}
      <UnggahBerkasModal
        opened={unggahBerkasOpen}
        onClose={() => setUnggahBerkasOpen(false)}
        batchUUID={unggahBerkasBatchUUID}
      />

      {/* Declaration PDF (PIB/PEB): final NOPEN once registered, else the CEISA draft */}
      <DeclarationPreviewModal
        opened={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        ajuNo={pdfPreviewAju}
      />
    </>
  );
}
